import React, { useEffect } from 'react'
import { NextPageContext } from 'next'
import { Store } from 'redux'
import classnames from 'classnames'

import getConfig from 'next/config'
const {
  publicRuntimeConfig: { defaultDescription, defaultArticleBanner },
} = getConfig()

import { withDispatch } from 'store'
import { getCategoryBySlug } from 'store/category'
import { getArticleBySlug, useArticleExt } from 'store/article'
import Container, { ArticleContainer, MainPanel, SidePanel, Row } from 'components/container'
import ContentLayout from 'components/layout/content'
import Sidebar from 'components/layout/sidebar'
import ArticleImage from 'components/article-image'

import ThemeToggler from 'components/theme-toggler'
import SocialShare from 'components/social-share'

import { event, set } from 'util/analytics'
import { ArticleExtType } from 'store/types/ts/article'

import { ArticleHelmet } from 'components/head/article-helmet'

import style from './article.module.scss'

interface Props {
  id: string
  delayInitialProps: boolean
}

const ArticlePage = ({ id }: Props) => {
  // Getting all article here to get all props
  const article: ArticleExtType = useArticleExt(id)

  let { category, author, topics } = article

  useEffect(() => {
    set({
      category: category.name,
      article: article.title,
      author: author ? author.name : null,
    })
    event({
      category: 'article',
      action: 'read',
      label: `${category.slug}/${article.slug}`,
    })
  }, [article, author, category])

  return (
    <>
      <ArticleHelmet
        article={article}
        category={category}
        author={author}
        topics={topics}
        defaultImage={defaultArticleBanner}
        defaultDescription={defaultDescription}
      />
      <ArticleContainer>
        <Container>
          <Row>
            <MainPanel key="main-panel">
              <ArticleImage
                image={article?.image}
                heroImage={article?.heroImage}
                category={category.id}
                slug={article.slug}
              />
              <div className={classnames('row', style.articleContent)}>
                <div className="col-12 col-lg-1">
                  <div className={style.leftButtons}>
                    <ThemeToggler />
                    <SocialShare path={`id/${article.id}/${article.slug}`} />
                  </div>
                </div>
                <div className="col-12 col-lg-11">
                  <ContentLayout id={id} categoryId={category.id} />
                </div>
              </div>
            </MainPanel>
            <SidePanel key="sidebar">
              <Sidebar id={id} />
            </SidePanel>
          </Row>
        </Container>
      </ArticleContainer>
    </>
  )
}

interface Context extends NextPageContext {
  store: Store
  fetchArticleContent: Function
  fetchCategoryArticlesBySlug: Function
  fetchMissing: Function
  setArticleHero: Function
}

ArticlePage.delayInitialProps = true

ArticlePage.getInitialProps = async ({
  query,
  fetchArticleContent,
  fetchCategoryArticlesBySlug,
  fetchMissing,
  setArticleHero,
  ...context
}: Context) => {
  const { store } = context
  const { category: categorySlug, article: articleSlug } = query

  const category = getCategoryBySlug(store, categorySlug)
  if (!category) {
    return {
      error: 404,
    }
  }
  let article = getArticleBySlug(store, articleSlug, category.id)
  if (!article) {
    // eslint-disable-next-line no-unused-vars
    const [found, ...rest] = await fetchCategoryArticlesBySlug({
      category: category.id,
      articles: [articleSlug],
    })
    article = found
    if (!article) {
      return {
        error: 404,
      }
    }
  }
  setArticleHero(article.id)
  await Promise.all([fetchArticleContent(article.id), fetchMissing([article])])
  await ContentLayout.getInitialProps({ ...context, id: article.id }) // The ContentLayout will parse all relevant links and fetch the missing articles.

  return { query: { article: articleSlug, category: categorySlug }, id: article.id }
}
export default withDispatch({
  article: ['fetchArticleContent'],
  category: ['fetchCategoryArticlesBySlug'],
  shared: ['fetchMissing'],
  hero: ['setArticleHero'],
})(ArticlePage)
